import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { LoginGate } from "./LoginGate";

const TYPES = { impegnativa: "Richiesta impegnativa", prescrizione: "Richiesta prescrizione", certificato: "Richiesta certificazione", documentazione: "Invio documentazione" };

export function RequestsList({ items }) {
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="requests-empty">Nessuna richiesta.</p>;
  return (
    <div className="space-y-2" data-testid="requests-list">
      {items.map((r) => (
        <div key={r.id} data-testid={`request-item-${r.id}`} className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between gap-2"><b className="text-sm text-slate-800">{r.type_label}</b><StatusBadge status={r.status} /></div>
          <p className="mt-1 text-xs text-slate-500">{fmtDateTime(r.created_at)} · {r.description}</p>
          {r.reviewer_name && <p className="mt-1 text-xs text-slate-600">Verificata da {r.reviewer_name}{r.reviewer_note ? `: ${r.reviewer_note}` : ""}</p>}
        </div>
      ))}
    </div>
  );
}

export function RequestsPanel() {
  const { user } = useAuth();
  const [type, setType] = useState("impegnativa");
  const [desc, setDesc] = useState("");
  const [docs, setDocs] = useState([]);
  const [att, setAtt] = useState([]);
  const [items, setItems] = useState([]);
  const load = () => api.get("/requests/mine").then(({ data }) => setItems(data));
  useEffect(() => {
    if (user) {
      load();
      api.get("/documents/mine").then(({ data }) => setDocs(data));
    }
  }, [user]);

  const submit = async () => {
    try {
      await api.post("/requests", { type, description: desc, document_ids: att });
      toast.success("Richiesta inviata con successo");
      setDesc("");
      setAtt([]);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-5" data-testid="requests-panel">
      <div className="flex gap-2 rounded-xl border border-sky-100 bg-sky-50 p-3 text-xs text-sky-900">
        <ShieldAlert className="h-4 w-4 shrink-0" /> Ogni richiesta viene verificata da un medico abilitato. L'AI non emette prescrizioni né certificazioni.
      </div>
      <Select value={type} onValueChange={setType}>
        <SelectTrigger data-testid="request-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
      <Textarea data-testid="request-description-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Descrivi la richiesta (es. rinnovo terapia abituale indicata dal medico)" />
      {docs.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-slate-600">Allega documenti dal tuo archivio</p>
          {docs.map((d) => (
            <label key={d.id} className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid={`request-attach-${d.id}`} checked={att.includes(d.id)} onChange={(e) => setAtt(e.target.checked ? [...att, d.id] : att.filter((x) => x !== d.id))} /> {d.filename}
            </label>
          ))}
        </div>
      )}
      {user ? <Button data-testid="request-submit-btn" disabled={desc.length < 5} onClick={submit} className="w-full rounded-full bg-sky-600 hover:bg-sky-700">Invia alla segreteria medica</Button> : <LoginGate />}
      {user && <RequestsList items={items} />}
    </div>
  );
}
